/** SUNTRONIX ONE — ONE Global Advisories. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Megaphone, AlertTriangle, ShieldAlert, CheckCircle2, RefreshCcw,
  ExternalLink, Plus, Send,
} from "lucide-react";

const CATEGORY_OPTS = [
  "stock_availability", "stock_constraint", "product_discontinuation",
  "price_revision", "oem_announcement", "regional_holiday",
  "warehouse_closure", "natural_calamity", "logistics_delay",
  "vendor_outage", "integration_incident", "security_advisory",
  "regulatory_update", "general_notice",
];
const SEVERITY_OPTS = ["critical", "high", "medium", "low", "information"];
const AUDIENCE_OPTS = [
  ["suntronix_only",         "Suntronix ops only"],
  ["all_master_clients",     "All Master Clients"],
  ["all_customers",          "All Customers"],
  ["specific_master_client", "Specific Master Client"],
  ["specific_customers",     "Specific Customers"],
  ["specific_countries",     "Specific Countries"],
];

// Category-aware defaults so the composer arrives 80% written for the
// most common advisory kinds (Price Revision, Regional Holiday etc).
const CATEGORY_TEMPLATES = {
  price_revision: {
    headline: "Price revision effective from <DATE> — <SCOPE>",
    summary:  "Prices for <PRODUCT / CATEGORY / OEM> will be revised effective <DATE>. Please review open quotations and pending orders.",
    description: [
      "Reason: OEM / logistics / FX-driven revision.",
      "Products affected: <list SKUs, brand or category>.",
      "Effective date: <DATE>.",
      "Impact on existing orders: <hold · honour · re-quote>.",
      "Action required from customer: acknowledge and reissue POs on the new rate card where necessary.",
    ].join("\n"),
    severity: "high",
    action_required: true,
    audience_type: "all_customers",
  },
  regional_holiday: {
    headline: "Regional holiday — reduced service window",
    summary: "Warehouse and last-mile services will operate at reduced capacity in <REGION> from <DATE> to <DATE>.",
    severity: "medium",
    action_required: false,
    audience_type: "all_customers",
  },
  warehouse_closure: {
    headline: "Warehouse closure — <FACILITY>",
    summary: "Facility <NAME> will be closed on <DATE> for <REASON>. Dispatches will resume <DATE>.",
    severity: "high",
    action_required: true,
    audience_type: "all_customers",
  },
  natural_calamity: {
    headline: "Natural calamity — service disruption in <REGION>",
    summary: "Operations in <REGION> may be disrupted due to <EVENT>. Deployments and pickups affected until <DATE>.",
    severity: "critical",
    action_required: true,
    audience_type: "all_customers",
  },
};

const SEV_TONE = {
  critical:     "text-red-800 bg-red-100 border-red-300 font-bold",
  high:         "text-red-700 bg-red-50 border-red-200",
  medium:       "text-amber-700 bg-amber-50 border-amber-200",
  low:          "text-slate-700 bg-slate-100 border-slate-200",
  information:  "text-slate-500 bg-slate-50 border-slate-200",
};

export default function Advisories() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(true);
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("");
  const [selected, setSelected] = useState(null);
  const [ackNote, setAckNote] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDraft, setComposeDraft] = useState(null);
  const canCompose = user && (user.role === "super_admin"
    || user.role === "suntronix_admin"
    || user.role === "suntronix_ops");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [list, sm] = await Promise.all([
        api.get("/advisories", { params: {
          category: category || undefined,
          severity: severity || undefined,
        } }),
        api.get("/advisories/summary"),
      ]);
      setRows(list.data.items || []);
      setSummary(sm.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, [category, severity]);

  useEffect(() => { load(); }, [load]);

  // Deep link: /advisories?compose=price_revision opens the composer
  // pre-filled with the category-aware template.
  useEffect(() => {
    const k = sp.get("compose");
    if (k && !composeOpen) {
      const tpl = CATEGORY_TEMPLATES[k] || {};
      setComposeDraft({
        headline: tpl.headline || "",
        summary:  tpl.summary  || "",
        description: tpl.description || "",
        category: CATEGORY_OPTS.includes(k) ? k : "general_notice",
        severity: tpl.severity || "information",
        action_required: !!tpl.action_required,
        audience_type: tpl.audience_type || "suntronix_only",
        effective_from: "",
        effective_until: "",
        source_name: "",
        source_reference: "",
      });
      setComposeOpen(true);
    }
  }, [sp, composeOpen]);

  const openCompose = (categoryKey = "general_notice") => {
    const tpl = CATEGORY_TEMPLATES[categoryKey] || {};
    setComposeDraft({
      headline: tpl.headline || "",
      summary:  tpl.summary  || "",
      description: tpl.description || "",
      category: categoryKey,
      severity: tpl.severity || "information",
      action_required: !!tpl.action_required,
      audience_type: tpl.audience_type || "suntronix_only",
      effective_from: "",
      effective_until: "",
      source_name: "",
      source_reference: "",
    });
    setComposeOpen(true);
  };

  const closeCompose = () => {
    setComposeOpen(false);
    setComposeDraft(null);
    if (sp.get("compose")) {
      sp.delete("compose"); setSp(sp, { replace: true });
    }
  };

  // Draft → verify → publish (each backend endpoint enforces auth).
  const submitAdvisory = async (mode) => {
    if (!composeDraft) return;
    if (!composeDraft.headline?.trim() || !composeDraft.category) {
      toast.error("Headline and category are required");
      return;
    }
    try {
      const { data: created } = await api.post("/advisories", composeDraft);
      const advId = created?.item?.id || created?.id;
      if (!advId) throw new Error("no id returned");
      if (mode === "publish") {
        await api.post(`/advisories/${advId}/verify`,
          { verified: true, notes: "Verified inline by creator" });
        await api.post(`/advisories/${advId}/publish`, {});
        toast.success("Advisory published to targeted audience");
      } else {
        toast.success("Draft saved — verify & publish from the record");
      }
      closeCompose();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save advisory");
    }
  };

  const acknowledge = async () => {
    if (!selected) return;
    try {
      await api.post(`/advisories/${selected.id}/acknowledge`,
        { comments: ackNote || null });
      toast.success("Acknowledged");
      setSelected(null); setAckNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Acknowledge failed");
    }
  };

  const markExpired = async () => {
    if (!selected) return;
    if (!window.confirm(`Mark "${selected.title || selected.category}" as expired?`)) return;
    try {
      await api.post(`/advisories/${selected.id}/expire`);
      toast.success("Advisory expired");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Expire failed");
    }
  };

  const kpis = useMemo(() => {
    if (!summary) return [];
    return [
      { key: "total",    label: "Active",   n: summary.total || 0, tone: "text-slate-900" },
      { key: "critical", label: "Critical", n: summary.by_severity?.critical || 0, tone: "text-red-800" },
      { key: "high",     label: "High",     n: summary.by_severity?.high || 0, tone: "text-red-700" },
      { key: "action",   label: "Action required", n: summary.action_required || 0, tone: "text-amber-700" },
    ];
  }, [summary]);

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="advisories-page">
      <PageHeader
        eyebrow="Operations · Global"
        title="ONE Global Advisories"
        context="Verified stock, price, holiday, calamity and logistics updates that may affect your operations."
        actions={
          <div className="flex items-center gap-2">
            {canCompose && (
              <Button onClick={() => openCompose("price_revision")}
                data-testid="adv-new">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> New advisory
              </Button>
            )}
            <Button variant="outline" onClick={load} disabled={busy}
              data-testid="adv-refresh">
              <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <div key={k.key} className="st-panel p-3"
                data-testid={`adv-kpi-${k.key}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{k.label}</div>
            <div className={`text-2xl font-heading font-semibold tabular-nums ${k.tone}`}>{k.n}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select className="border rounded h-9 px-2 text-[12px]"
          value={category} onChange={(e) => setCategory(e.target.value)}
          data-testid="adv-category">
          <option value="">All categories</option>
          {["stock_availability","stock_constraint","price_revision","regional_holiday",
            "warehouse_closure","natural_calamity","logistics_delay","vendor_outage",
            "integration_incident","security_advisory","regulatory_update"].map((c) => (
            <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
          ))}
        </select>
        <select className="border rounded h-9 px-2 text-[12px]"
          value={severity} onChange={(e) => setSeverity(e.target.value)}
          data-testid="adv-severity">
          <option value="">All severities</option>
          {["critical","high","medium","low","information"].map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <div className="text-[11px] text-slate-500 ml-auto">{rows.length} advisories</div>
      </div>

      <Section title="Active advisories" testId="adv-list">
        {rows.length === 0 && !busy && (
          <div className="st-panel p-8 text-center text-[12px] text-slate-400"
                data-testid="adv-empty">
            No active advisories — all clear.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((a) => (
            <button key={a.id}
                       onClick={() => setSelected(a)}
                       className="st-panel p-4 text-left hover:border-slate-400 transition"
                       data-testid={`adv-card-${a.id}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded border font-semibold ${SEV_TONE[a.severity] || SEV_TONE.information}`}>
                  {a.severity}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-slate-500">
                  {a.category?.replace(/_/g, " ")}
                </span>
                {a.action_required && (
                  <span className="ml-auto text-[9px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                    Action required
                  </span>
                )}
              </div>
              <div className="text-[14px] font-semibold text-slate-900">{a.headline}</div>
              {a.summary && (
                <div className="text-[12px] text-slate-600 mt-1 line-clamp-2">{a.summary}</div>
              )}
              <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-2">
                {a.source_name && <span>Source · {a.source_name}</span>}
                {a.effective_until && <span>· until {a.effective_until.slice(0,10)}</span>}
              </div>
            </button>
          ))}
        </div>
      </Section>

      {/* Composer — Suntronix ops only.  Opens via /advisories?compose=price_revision */}
      <Dialog open={composeOpen} onOpenChange={(v) => { if (!v) closeCompose(); }}>
        <DialogContent className="max-w-2xl" data-testid="adv-composer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-amber-600" strokeWidth={1.6} />
              New Global Advisory
            </DialogTitle>
            <div className="text-[11px] text-slate-500">
              Draft, verify and publish an operational advisory to targeted audiences.
            </div>
          </DialogHeader>
          {composeDraft && (
            <div className="space-y-3 text-[13px]">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Category</div>
                  <select className="w-full border rounded h-9 px-2 text-[13px]"
                    value={composeDraft.category}
                    onChange={(e) => {
                      const k = e.target.value;
                      const tpl = CATEGORY_TEMPLATES[k] || {};
                      setComposeDraft((d) => ({
                        ...d, category: k,
                        headline: tpl.headline || d.headline,
                        summary:  tpl.summary  || d.summary,
                        description: tpl.description || d.description,
                        severity: tpl.severity || d.severity,
                        action_required: tpl.action_required ?? d.action_required,
                        audience_type: tpl.audience_type || d.audience_type,
                      }));
                    }}
                    data-testid="adv-c-category">
                    {CATEGORY_OPTS.map((c) => (
                      <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Severity</div>
                  <select className="w-full border rounded h-9 px-2 text-[13px]"
                    value={composeDraft.severity}
                    onChange={(e) => setComposeDraft((d) => ({ ...d, severity: e.target.value }))}
                    data-testid="adv-c-severity">
                    {SEVERITY_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Headline</div>
                <Input value={composeDraft.headline}
                  onChange={(e) => setComposeDraft((d) => ({ ...d, headline: e.target.value }))}
                  placeholder="Price revision effective from 15 Apr 2026 — Apple lineup"
                  data-testid="adv-c-headline" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Summary</div>
                <Textarea rows={2} value={composeDraft.summary}
                  onChange={(e) => setComposeDraft((d) => ({ ...d, summary: e.target.value }))}
                  data-testid="adv-c-summary" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Description (impact + actions)</div>
                <Textarea rows={5} value={composeDraft.description}
                  onChange={(e) => setComposeDraft((d) => ({ ...d, description: e.target.value }))}
                  data-testid="adv-c-desc" />
              </label>
              <div className="grid grid-cols-3 gap-3">
                <label className="block col-span-3 sm:col-span-1">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Audience</div>
                  <select className="w-full border rounded h-9 px-2 text-[13px]"
                    value={composeDraft.audience_type}
                    onChange={(e) => setComposeDraft((d) => ({ ...d, audience_type: e.target.value }))}
                    data-testid="adv-c-audience">
                    {AUDIENCE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Effective from</div>
                  <Input type="date" value={composeDraft.effective_from || ""}
                    onChange={(e) => setComposeDraft((d) => ({ ...d, effective_from: e.target.value }))}
                    data-testid="adv-c-from" />
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Effective until</div>
                  <Input type="date" value={composeDraft.effective_until || ""}
                    onChange={(e) => setComposeDraft((d) => ({ ...d, effective_until: e.target.value }))}
                    data-testid="adv-c-until" />
                </label>
              </div>
              <label className="inline-flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={composeDraft.action_required}
                  onChange={(e) => setComposeDraft((d) => ({ ...d, action_required: e.target.checked }))}
                  data-testid="adv-c-action" />
                Recipient action required (chases + acknowledgement)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Source name</div>
                  <Input value={composeDraft.source_name || ""}
                    onChange={(e) => setComposeDraft((d) => ({ ...d, source_name: e.target.value }))}
                    placeholder="Apple India · OEM circular"
                    data-testid="adv-c-source" />
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Source URL / ref.</div>
                  <Input value={composeDraft.source_reference || ""}
                    onChange={(e) => setComposeDraft((d) => ({ ...d, source_reference: e.target.value }))}
                    placeholder="https://…"
                    data-testid="adv-c-source-url" />
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={closeCompose} data-testid="adv-c-cancel">Cancel</Button>
            <Button variant="outline" onClick={() => submitAdvisory("draft")}
              data-testid="adv-c-draft">Save draft</Button>
            <Button onClick={() => submitAdvisory("publish")}
              data-testid="adv-c-publish">
              <Send className="h-3.5 w-3.5 mr-1.5" /> Verify &amp; publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); setAckNote(""); } }}>
        <DialogContent className="max-w-xl" data-testid="adv-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.headline}</DialogTitle>
            <div className="text-[12px] text-slate-500">
              {selected?.category?.replace(/_/g, " ")} · {selected?.severity}
            </div>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-[13px]">
              {selected.summary && <p>{selected.summary}</p>}
              {selected.description && <p className="text-slate-600">{selected.description}</p>}
              {selected.source_reference && (
                <a href={selected.source_reference} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[12px] text-sky-700 hover:underline">
                  <ExternalLink className="h-3 w-3" /> Source reference
                </a>
              )}
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <F label="Source">{selected.source_name || "—"}</F>
                <F label="Effective from">{selected.effective_from || "—"}</F>
                <F label="Effective until">{selected.effective_until || "—"}</F>
                <F label="Verified">{selected.verified ? "Yes" : "No"}</F>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  Acknowledge (optional note)
                </div>
                <Input value={ackNote} onChange={(e) => setAckNote(e.target.value)}
                  placeholder="Note visible to Suntronix"
                  data-testid="adv-ack-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelected(null)}>Close</Button>
            {selected?.status === "published" && (
              <Button variant="outline" onClick={markExpired} data-testid="adv-mark-expired">
                Mark expired
              </Button>
            )}
            <Button onClick={acknowledge} data-testid="adv-ack-submit">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Acknowledge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-[13px]">{children ?? "—"}</div>
    </div>
  );
}


// ---------- Dashboard card (compact summary) ----------
export function AdvisoriesDashboardCard() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/advisories", { params: { active_only: true } });
        setRows((data.items || []).slice(0, 4));
      } catch { /* silent */ } finally { setBusy(false); }
    })();
  }, []);
  if (busy || rows.length === 0) return null;
  return (
    <div className="st-panel p-4" data-testid="adv-dashcard">
      <div className="flex items-center gap-2 mb-2">
        <Megaphone className="h-4 w-4 text-amber-700" />
        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
          Global updates
        </div>
        <a href="/advisories" className="ml-auto text-[10px] text-sky-700 hover:underline">
          View all →
        </a>
      </div>
      <ul className="space-y-2">
        {rows.map((a) => (
          <li key={a.id} className="text-[12px]">
            <div className="flex items-center gap-1.5">
              <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border font-semibold ${SEV_TONE[a.severity]}`}>
                {a.severity}
              </span>
              <a href="/advisories" className="text-slate-800 hover:underline">{a.headline}</a>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
